/**
 * The piece.
 *
 * Everything Phases 0-8 built exists to make this page possible, and until now
 * it did not exist: the caption line was one `<div>` between a landmark overlay
 * and a session recorder, on a page you had to scroll past sixty sliders to
 * reach. That is the right arrangement for judging the machinery and the wrong
 * one for watching it work.
 *
 * What this page is:
 *
 *   - one line of type, sliding left, anchored so the newest glyph never moves
 *   - each character carrying the mouth that made it — weight from how far the
 *     jaw was open, width from smile against pucker, GRAD from voice quality
 *   - the sounds ASR discards drawn as marks, inline, at the moment they
 *     happened, faded by how sure the camera was
 *   - a word both channels claim faded too, because that is a real doubt and
 *     hiding it would be a lie about which one was right
 *
 * There is no readout, no meter and no overlay. If something is wrong the lab is
 * one link away and shares this page's tuning through the URL.
 *
 * The video element is off-screen rather than hidden: `getUserMedia` needs
 * somewhere to put the stream, and a `display: none` video stops producing
 * frames in some browsers — which would take the vision gate down without
 * anything reporting it.
 */

import '@fontsource-variable/roboto-flex/full.css';
import './style.css';

import { config } from './core/config.js';
import { Presets } from './core/presets.js';
import { applyTuning, decodeTuning, encodeTuning, resetTuning, tuning } from './core/tunables.js';
import { Engine, uploadSession } from './engine.js';
import { Caption, charElement, wordElement } from './render/caption.js';
import { Emitter } from './render/emitter.js';
import { downloadCaptionSVG } from './render/exportsvg.js';
import { boxFor, coverRect, mouthAnchor, SmoothAnchor } from './render/facemap.js';
import { glyphFor } from './render/gestureglyph.js';
import { Probe } from './render/probe.js';
import { Ticker } from './render/ticker.js';
import { matchLanguage } from './speech/languages.js';
import { charTime } from './speech/transcript.js';
import { Controls } from './ui/controls.js';

/**
 * Where the tuning comes from on arrival, in order of who wins.
 *
 *   1. the URL fragment — somebody said this one explicitly, by opening a link
 *   2. what was on screen last time, off this machine
 *   3. the shipped defaults
 *
 * The URL first because it is the only one that can have been *meant*: a link
 * carries an intention, and silently overriding it with local state would make
 * a shared tuning open differently for the person who sent it. Restoring last
 * time's is only ever filling a vacuum.
 *
 * Not a preset *name* at step 2, but the tuning itself — see presets.js. What
 * should come back is what was on screen, which is usually a saved setting with
 * two things nudged since, and a name would restore the setting and lose the
 * nudges.
 */
const presets = new Presets();
const fromUrl = decodeTuning(window.location.hash);
const startedFromUrl = Object.keys(fromUrl).length > 0;
const tuned = applyTuning(startedFromUrl ? fromUrl : presets.last);

/**
 * Which language Channel A opens on, and — recorded — why.
 *
 *   url      the fragment named one, so somebody meant it
 *   saved    it was chosen here before, default-valued or not
 *   browser  nothing was chosen, so follow `navigator.language`
 *   default  the browser has no language this build offers
 *
 * The `saved` step is separate from the restored tuning above, and has to be.
 * A tuning is a diff against the shipped defaults, so a language that *is* the
 * default disappears from it — which made "I explicitly chose English" and "I
 * never chose" the same state, and a browser set to Portuguese overrode a
 * deliberate choice on every reload. The explicit choice is stored on its own.
 *
 * `en-US` stays the shipped default regardless: `defaults` is a JSON snapshot
 * that has to be identical on every machine, or a recording's tuning diff means
 * something different depending on who made it. Everything here is an opening
 * move on top of that, which is why the language shows in the drawer as a moved
 * control whenever it is not English. It is one.
 */
function openingLanguage() {
  if (tuned.applied.includes('speech.lang')) return startedFromUrl ? 'url' : 'saved';

  const chosen = matchLanguage(presets.lang);
  if (chosen) {
    config.speech.lang = chosen;
    return 'saved';
  }
  const local = matchLanguage(navigator.language);
  if (local) {
    config.speech.lang = local;
    return 'browser';
  }
  return 'default';
}

const languageSource = openingLanguage();

const video = document.getElementById('video');
const lineEl = document.getElementById('line');
const hintEl = document.getElementById('hint');
const goBtn = document.getElementById('go');
const stateEl = document.getElementById('state');
const barEl = document.getElementById('bar');
const viewBtn = document.getElementById('view');

const viewportEl = document.getElementById('viewport');
const caption = new Caption(lineEl);
const ticker = new Ticker({
  viewport: viewportEl,
  track: document.getElementById('track'),
  caption,
});

// ── The face view ───────────────────────────────────────────────────────────

/**
 * Text anchored to the mouth that made it.
 *
 * The line view puts the newest glyph at a fixed column and slides everything
 * left of it. This puts the newest glyph at the speaker's lips instead — so the
 * words appear where they were actually produced, and the whole line moves as
 * the head does. Everything else is unchanged: the same caption, the same
 * per-glyph axes, the same gestures placed at the moment they happened.
 *
 * Two things make it read as speech rather than as a caption overlay. The type
 * is sized from the inter-ocular distance, so it is the same size relative to
 * the *face* however close the speaker sits — leaning in makes the words grow.
 * And the anchor is smoothed, because landmarks jitter a pixel or two on a still
 * face and type pinned straight to them shimmers.
 *
 * A view rather than a replacement. The plain line is better for reading a long
 * utterance back; this is better for watching one happen.
 */
const anchor = new SmoothAnchor();
const emitEl = document.getElementById('emit');
const emitter = new Emitter(emitEl, { config: config.emit });
// Lives inside the emit layer, so it is parked in exactly the coordinates the
// words are launched in. See the comment on the element in perform/index.html.
const probe = new Probe(document.getElementById('probe'), { config });

/**
 * Three ways to put the same line on the screen.
 *
 *   line   a ticker at a fixed column — best for reading a long utterance back
 *   face   the same line, pinned to the mouth, travelling with the head
 *   mouth  the mouth as an emitter: words leave the lips and drift away
 *
 * The third is the one SPEC1.md files under "later: breath emission", with the
 * words themselves as the particles. It is the only view where the line stops
 * being a line — which is also why the other two are still here.
 */
const VIEWS = ['line', 'face', 'mouth'];
const VIEW_LABEL = { line: 'face view', face: 'mouth view', mouth: 'line view' };

let view = VIEWS.includes(new URLSearchParams(window.location.search).get('view'))
  ? new URLSearchParams(window.location.search).get('view')
  : 'line';
let lastType = 0;
let lastDim = null;
let lastGestures = null;

function setView(next) {
  view = VIEWS.includes(next) ? next : 'line';
  const camera = view !== 'line';
  document.body.classList.toggle('view-face', view === 'face');
  document.body.classList.toggle('view-mouth', view === 'mouth');
  document.body.classList.toggle('view-camera', camera);
  viewBtn.textContent = VIEW_LABEL[view];

  anchor.reset();
  ticker.reset();
  emitter.clear();
  emitQueue.length = 0;
  // The camera views resize the video from 2px to full-bleed and give `#stage`
  // a containing block, so both boxes the anchor is measured between move.
  invalidateBox();
  // Words already spoken are not re-launched on a view change: the queue is
  // caught up to the transcript rather than replayed, or switching mid-sentence
  // would fire everything ever said out of the speaker's mouth at once.
  queued = engine.transcript.committed.length;

  // Shareable, and separate from the tuning fragment on purpose: which view you
  // are in is not a threshold, and putting it there would have `applyTuning`
  // report it as an unrecognised control.
  const url = new URL(window.location.href);
  if (view === 'line') url.searchParams.delete('view');
  else url.searchParams.set('view', view);
  history.replaceState(null, '', url);
  if (view !== 'face') lineEl.style.fontSize = '';
}

// ── Launching words out of the mouth ────────────────────────────────────────

/** Committed words already handed to the queue. */
let queued = 0;
/**
 * What is waiting to leave the mouth, in spoken order.
 *
 * `i` is the index of the character to launch, or -1 for the whole word. The
 * mode is decided when an entry is *queued* rather than when it is drained, so
 * flipping the control mid-take lets what is already in flight finish as it was
 * scheduled instead of half a sentence changing shape.
 *
 * @type {{word: object, i: number, due: number}[]}
 */
const emitQueue = [];
let lastEmitAt = 0;

/**
 * Queue new words, and launch the ones that are due.
 *
 * Scheduled by when the word was *spoken* plus LAG, not by when it turned up.
 * Chrome finalises a whole phrase at once, so ten words can arrive in a single
 * event having been said over four seconds; launching them on arrival would fire
 * the lot as one clump. Using `tBind` spreads them back out over the time they
 * actually took — the same clock discipline the rest of the project runs on.
 *
 * Words whose moment has already passed still leave one at a time, `MIN_GAP`
 * apart, because a two-second-late finalisation is otherwise a clump again.
 */
function pumpEmitter(now, at) {
  const committed = engine.transcript.committed;
  // The transcript was cleared out from under us.
  if (committed.length < queued) {
    queued = committed.length;
    emitQueue.length = 0;
  }
  while (queued < committed.length) {
    const word = committed[queued++];
    if (!config.emit.PER_LETTER) {
      emitQueue.push({ word, i: -1, due: word.tBind + config.LAG });
      continue;
    }
    // Spread back over the time the word actually took, by exactly the formula
    // Phase 2 used to sample each character against the face. A letter that left
    // at a different moment from the one it was measured at would be carrying
    // somebody else's mouth.
    const n = word.chars.length;
    for (let i = 0; i < n; i++) {
      emitQueue.push({ word, i, due: charTime(word.tBind, word.dur, i, n) + config.LAG });
    }
  }

  if (!at) return; // no face, nowhere to launch from
  // A word gap would throttle letters: at speech.WORD_DUR_PER_CHAR they are
  // ~70 ms apart and MIN_GAP is 90.
  const gap = config.emit.PER_LETTER ? config.emit.LETTER_GAP : config.emit.MIN_GAP;
  while (emitQueue.length && emitQueue[0].due <= now && now - lastEmitAt >= gap) {
    launchItem(emitQueue.shift(), at);
    lastEmitAt = now;
  }
}

/**
 * How open the mouth was when this word was said.
 *
 * Straight out of the mouth buffer at the word's own bind time — the same
 * lookup Phase 2 does to shape the glyphs. So a word shouted with a wide "ah"
 * is both heavier and faster, from two independent readings of one mouth, which
 * is the thing this whole project is trying to show.
 */
function apertureAt(t) {
  const sample = engine.mouthBuffer.sampleAt(t);
  return sample?.aperture ?? 0.35;
}

/**
 * One queue entry out of the mouth.
 *
 * A letter takes its launch speed from `char.ap` directly rather than from
 * another buffer lookup. That is the same resolved, smoothed aperture that set
 * the glyph's weight — so a letter thrown hard is a letter drawn heavy, from one
 * reading of one mouth rather than two that can disagree by a frame. Phase 2
 * already did the lookup; doing it again here would be a second, worse answer.
 */
function launchItem({ word, i }, at) {
  if (i < 0) {
    emitter.emit(wordElement(word, true), at, apertureAt(word.tBind));
    return;
  }
  const char = word.chars[i];
  emitter.emit(charElement(char), at, char.ap ?? 0.35);
}

/**
 * The video's box in the emit layer's coordinates, measured rather than assumed.
 *
 * This used to pass the *layer's* size to `coverRect`, which is wrong in a way
 * nothing could report: the video is fixed to the window and the layer is not,
 * so in the mouth view a ~180 px flow element was standing in for a ~860 px
 * picture. Same cover scale, same x, and a launch point a few hundred pixels
 * above the mouth — which looks exactly like a bad landmark index.
 *
 * Cached, because two `getBoundingClientRect()` calls in the frame loop are two
 * forced layouts a frame. Invalidated by everything that can move either box;
 * if that list is ever incomplete the probe parks off the lips, which is the
 * failure being visible instead of silent for once.
 */
let anchorBox = null;

function measureBox() {
  if (anchorBox) return anchorBox;
  const box = boxFor(video.getBoundingClientRect(), emitEl.getBoundingClientRect());
  // A zero box is a measurement taken before the layout it describes exists —
  // the frame after a view change, or a video the browser has not sized yet.
  // Caching one would be permanent: nothing in the invalidation list fires
  // again, and the anchor would stay null for the rest of the take.
  if (!(box.boxW > 0 && box.boxH > 0)) return box;
  anchorBox = box;
  return box;
}

const invalidateBox = () => {
  anchorBox = null;
};

window.addEventListener('resize', invalidateBox);
window.addEventListener('orientationchange', invalidateBox);
video.addEventListener('loadedmetadata', invalidateBox);

/**
 * Where the mouth is, in emit-layer pixels, or null when there is nothing to ask.
 *
 * The video fills its box under `object-fit: cover`, so a landmark's normalised
 * y is not a fraction of the box — see facemap.js, which is where that maths
 * lives and is tested.
 */
function faceAnchor() {
  const box = measureBox();
  const rect = coverRect(video.videoWidth, video.videoHeight, box.boxW, box.boxH);
  if (!rect) return null;
  const found = mouthAnchor(engine.landmarker.lastLandmarks, config.vision.idx, rect, {
    mirrored: true,
    boxW: box.boxW,
    dx: box.dx,
    dy: box.dy,
  });
  return anchor.update(found, config.render.FACE_ANCHOR_EASE, {
    jump: config.render.FACE_ANCHOR_JUMP,
    giveUp: config.render.FACE_ANCHOR_GIVE_UP,
  });
}

/**
 * The last frame the audio graph had something to say about.
 *
 * `onFrame` reports null when it has nothing honest to report, which is not the
 * same as silence. Feeding that straight to the probe would snap GRAD back to
 * neutral on every such frame and read as the axis flickering rather than as
 * the voice changing.
 */
let lastAudio = null;

function drawFrame(now, audioFrame) {
  if (audioFrame) lastAudio = audioFrame;

  // Always ticked, in every view. It is two layout reads, and it is what prunes
  // the caption — a hidden line that never advanced would grow without bound
  // for as long as the take lasted.
  const at = view === 'line' ? null : faceAnchor();

  // Every view, and whether or not there is a face. It parks in the middle
  // marked `no face` until it has something to point at, because a probe that
  // hides when it has nothing to say is indistinguishable from a probe that was
  // never wired up — which is exactly how this one was first read.
  probe.frame(at, engine.mouthBuffer.latest(), lastAudio);
  // Every view: the marks sit on the line, and every view is showing some
  // arrangement of the line.
  paintGestures();

  if (view === 'mouth') {
    ticker.frame();
    pumpEmitter(now, at ? { x: at.x, y: at.y, iod: at.iod } : null);
    emitter.frame(now);
    paintDim();
    return;
  }

  if (view === 'face' && at) {
    const size = at.iod * config.render.FACE_TYPE_SCALE;
    // Writing font-size forces a reflow of the whole line, so only when it has
    // actually moved. Half a pixel is well below anything visible and well
    // above the jitter left after smoothing.
    if (Math.abs(size - lastType) > 0.5) {
      lineEl.style.fontSize = `${size}px`;
      lastType = size;
    }
    paintDim();
    ticker.frame({ x: at.x, y: at.y + at.iod * config.render.FACE_DROP });
    return;
  }

  // No face yet, or none any more. Falling back to the fixed anchor keeps the
  // words legible instead of parking them wherever the head was last seen.
  if (view !== 'line') paintDim();
  ticker.frame();
}

/**
 * The camera dim is a slider, so it has to reach CSS somehow. Written only when
 * it moves, because setting a custom property invalidates style for the whole
 * subtree it sits on.
 */
function paintDim() {
  const dim = config.render.FACE_DIM;
  if (dim === lastDim) return;
  document.body.style.setProperty('--face-dim', String(dim));
  lastDim = dim;
}

/**
 * The gesture marks, hidden by a class rather than by not drawing them.
 *
 * A switch that only stopped the next mark would leave the last fifty on the
 * line, which is not what off means — and turning it back on would show the
 * ones from after the toggle but not before. The class hides both, and the
 * detector, the timeline and the recording are all untouched: what is being
 * changed is whether Channel B is drawn, not whether it runs.
 */
function paintGestures() {
  const on = config.render.GESTURES;
  if (on === lastGestures) return;
  document.body.classList.toggle('no-gestures', !on);
  lastGestures = on;
}

/**
 * What the status line says, without turning into a diagnostic panel.
 *
 * Two states are worth a person's attention while performing: it is recording,
 * or something has stopped — it cannot see you, a channel died, an upload
 * failed. Everything else — delegate, frame rate, clock offset, constraint
 * negotiation — belongs in the lab and would only be noise here.
 *
 * Idle says nothing at all. It used to read `idle`, which is a label for the
 * state of nothing happening, on a page whose whole point is that there is
 * nothing on it. The element collapses when empty, so the bar closes up around
 * it rather than holding a gap.
 */
const trouble = new Map();

function paintState() {
  const was = stateEl.textContent;
  if (trouble.size) {
    stateEl.textContent = [...trouble.values()].join(' · ');
    stateEl.className = 'mono warn';
  } else {
    stateEl.textContent = engine.recorder.recording ? '● recording' : '';
    stateEl.className = 'mono dim';
  }
  // The status is its own row and collapses when empty, so going from silent to
  // saying something makes the bar a line taller — which makes `#stage` a line
  // shorter, and `#stage` is the box the mouth anchor is measured against. Only
  // on the transition, which is what `was` is for: this runs on every channel
  // state change and re-measuring on each would put a forced layout in the path
  // of an error message.
  if (!was !== !stateEl.textContent) invalidateBox();
}

const engine = new Engine({
  video,
  onState: (channel, state, detail) => {
    if (channel === 'face') {
      if (state === 'lost') trouble.set('face', 'no face — look at the camera');
      else trouble.delete('face');
    } else if (state === 'silent') {
      // Being spoken to and returning nothing. Names the language, because that
      // is what it nearly always turns out to be.
      trouble.set('asr', `no transcript — ${detail}`);
    } else if (String(state).startsWith('model-')) {
      trouble.set('asr-model', detail);
    } else if (state === 'error') {
      trouble.set(channel, `${channel}: ${detail ?? 'stopped'}`);
    } else if (state === 'stalled') {
      trouble.set(channel, `${channel} stalled, recovering`);
    } else if (state === 'running' || state === 'listening') {
      trouble.delete(channel);
    }
    paintState();
  },
  onResult: () => {
    // A word arriving is the only proof the recogniser is alive, so it is what
    // clears the silence warning — a `listening` state does not, because being
    // listening and returning nothing is exactly the failure.
    if (trouble.delete('asr')) paintState();
    caption.render(engine.transcript);
  },
  // Released in spoken order, LAG behind reality. Straight onto the line at the
  // moment it happened — which is the whole architecture, and it costs one line
  // here because Timeline has done the ordering since Phase 0.
  onRelease: (event) => {
    if (!event.type) return;
    // Always placed on the line, so switching views does not lose them. In the
    // mouth view a copy is launched as well, from the lips, at the confidence
    // the camera gave it — the marks come out of the mouth like the words do.
    caption.addGesture(event);
    // Launched only when they are being shown. The CSS would hide the particle
    // anyway, but an invisible one still counts against `emit.MAX` and would
    // retire a word that could have been seen.
    if (view === 'mouth' && anchor.ready && config.render.GESTURES) {
      const glyph = glyphFor(event);
      if (!glyph) return;
      const wrap = document.createElement('span');
      wrap.className = 'cap-gesture';
      wrap.append(glyph);
      emitter.emit(wrap, { x: anchor.x, y: anchor.y, iod: anchor.iod }, apertureAt(event.t));
    }
  },
  onFrame: ({ now, audioFrame }) => drawFrame(now, audioFrame),
});

// Decided before the engine existed, so it is handed over rather than asked for.
engine.languageSource = languageSource;

engine.start();

// ── Begin / end ─────────────────────────────────────────────────────────────

let busy = false;

async function begin() {
  if (busy) return;
  busy = true;
  goBtn.disabled = true;
  try {
    const failures = await engine.startAll({ note: 'perform' });
    for (const f of failures) trouble.set(f.split(':')[0], f);
    // Hiding the hint reflows `#stage`, which is the emit layer's containing
    // block — so the box the anchor is offset against has just moved.
    hintEl.hidden = true;
    invalidateBox();
    goBtn.textContent = 'end';
    paintState();
  } finally {
    goBtn.disabled = false;
    busy = false;
  }
}

function end() {
  const session = engine.stopAll();
  goBtn.textContent = 'begin';
  paintState();
  // Every recording goes to R2 — that has been true since Phase 4 and it does
  // not stop being true because this page is prettier. A failure is reported and
  // the take stays in memory for the lab to pick up.
  if (session) {
    uploadSession(session).catch((err) => {
      trouble.set('upload', `upload failed: ${err.message ?? err}`);
      paintState();
    });
  }
}

const toggle = () => (engine.capturing ? end() : begin());

const nextView = () => VIEWS[(VIEWS.indexOf(view) + 1) % VIEWS.length];

viewBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  setView(nextView());
});

goBtn.addEventListener('click', toggle);

// ── The bar ─────────────────────────────────────────────────────────────────

/**
 * Dismissing the chrome, and getting it back.
 *
 * It used to fade to 12% whenever a take was running and return on hover, which
 * made the controls a thing that happened *to* you. Now it is always on the
 * bottom and always there, and hiding it is a decision: swipe the bar either
 * way, press the handle, or `c`.
 *
 * The handle is deliberately left on screen when dismissed. A phone has no `c`
 * key and tapping the stage changes the view now, so a bar that hid completely
 * would be a control with no way back.
 */
const setChrome = (hidden) => document.body.classList.toggle('chrome-hidden', hidden);
const toggleChrome = () => setChrome(!document.body.classList.contains('chrome-hidden'));

document.getElementById('bar-handle').addEventListener('click', (e) => {
  e.stopPropagation();
  toggleChrome();
});

/**
 * Swipe the bar up or down to dismiss it, and either way to bring it back.
 *
 * Gestures that start on a control are left alone: a drag that begins on `begin`
 * is a missed press, not a swipe, and swallowing it would make the button feel
 * broken. The bar's own background is the grab area.
 */
let swipeFrom = null;

barEl.addEventListener('pointerdown', (e) => {
  swipeFrom = e.target.closest('button, a') ? null : e.clientY;
});

barEl.addEventListener('pointerup', (e) => {
  if (swipeFrom === null) return;
  const travelled = Math.abs(e.clientY - swipeFrom);
  swipeFrom = null;
  // Far enough that it cannot be a tap that wobbled.
  if (travelled >= 24) toggleChrome();
});

barEl.addEventListener('pointercancel', () => {
  swipeFrom = null;
});

document.addEventListener('keydown', (e) => {
  if (e.target.matches('input, button, a')) return;
  if (e.code === 'Space') {
    e.preventDefault();
    toggle();
  }
  if (e.key === 'Escape') closeDrawer();
  if (e.key === 'v') setView(nextView());
  if (e.key === 'c') toggleChrome();
});

// Tapping the stage changes the view. It used to start and stop the take, which
// put the most destructive action on this page behind the easiest gesture on it —
// and there was no way to change view on a phone without reaching for the bar.
// Beginning is the bar's job now, and the bar no longer hides on its own.
//
// Not while the settings sheet is open, though: on a phone the sheet covers most
// but not all of the stage, and the strip left showing is exactly where a thumb
// rests.
document.getElementById('stage').addEventListener('click', () => {
  if (!drawer.hidden) return;
  setView(nextView());
});

// ── The settings drawer ─────────────────────────────────────────────────────

const drawer = document.getElementById('drawer');
const tuneState = document.getElementById('tune-state');

let urlTimer = 0;

function publishTuning() {
  clearTimeout(urlTimer);
  urlTimer = setTimeout(() => {
    const current = tuning();
    const encoded = encodeTuning(current);
    // The query string carries the view, and passing a bare pathname to
    // replaceState would drop it — so an empty tuning has to name the search
    // explicitly rather than let the URL collapse to the path.
    const bare = window.location.pathname + window.location.search;
    history.replaceState(null, '', encoded ? `#${encoded}` : bare);
    // The link to the lab carries the tuning with it, so crossing over does not
    // silently reset every threshold you just set by ear.
    document.getElementById('tolab').href = encoded ? `/#${encoded}` : '/';
    // Debounced with the URL rather than written per change: a slider sweep is
    // forty-five events and one decision, and this is what a refresh restores.
    presets.setLast(current);
  }, 250);
}

function updateTuneState() {
  const n = Object.keys(tuning()).length;
  const where = presets.available ? 'in the URL, and kept' : 'in the URL';
  tuneState.textContent = n ? `${n} moved · ${where}` : 'all at defaults';
}

/**
 * Channel A reads its language once, when the recogniser is built.
 *
 * `rec.lang` is set inside `_build()`, which only runs on `start()` — so a new
 * language does nothing at all until the recogniser is replaced. Doing that here
 * rather than making the user end and restart the take: a control that appears to
 * do nothing is the one failure this project keeps deciding is unacceptable.
 *
 * Only Channel A stops. The camera, the audio DSP, the timeline and the recording
 * all keep running, so the take survives it — the transcript just has a gap the
 * length of a recogniser handshake.
 */
function relanguage() {
  if (!engine.recognition.listening) return;
  engine.recognition.stop();
  engine.recognition.start();
}

/**
 * A language picked here, remembered, and made to take effect.
 *
 * Stored whether or not it differs from the shipped default — that is the whole
 * point of it living outside the tuning diff. From here on this machine opens on
 * what was last chosen rather than on what the browser happens to be set to.
 */
function chooseLanguage(value) {
  presets.setLang(value);
  engine.languageSource = 'saved';
  relanguage();
}

const controls = new Controls(document.getElementById('tune'), {
  onChange: (field, value) => {
    publishTuning();
    updateTuneState();
    engine.recorder.event(`tune:${field.path}=${value}`);
    if (field.path === 'speech.lang') chooseLanguage(value);
  },
  onBulkChange: () => {
    publishTuning();
    updateTuneState();
    engine.recorder.event('tune:reset');
    // Reset puts the language back to the shipped default too, and that counts
    // as choosing it: otherwise a reset would look like English and then open in
    // the browser's language next time, which is the surprise this whole
    // separate field exists to remove.
    chooseLanguage(config.speech.lang);
  },
});

document.getElementById('tune-reset-all').addEventListener('click', () => controls.resetAll());

// ── Saved settings ──────────────────────────────────────────────────────────

/**
 * Named tunings, so the same look can be got back without finding it by ear.
 *
 * The dropdown applies on selection rather than behind a "load" button: every
 * other control on this page takes effect the moment it moves, and a setting you
 * have to confirm is a setting you cannot audition. Nothing is lost by it —
 * whatever was on screen is one `reset` or one re-selection away, and the URL
 * still holds it until the next change.
 */
const presetEl = document.getElementById('preset');
const presetName = document.getElementById('preset-name');
const presetSave = document.getElementById('preset-save');
const presetDelete = document.getElementById('preset-delete');

function paintPresets(selected = presetEl.value) {
  const names = presets.names();
  const options = ['<option value="">saved settings…</option>'];
  for (const name of names) {
    // Escaped, because a name is typed by a person and goes back into markup.
    const safe = name.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    options.push(`<option value="${safe}">${safe}</option>`);
  }
  presetEl.innerHTML = options.join('');
  presetEl.value = names.includes(selected) ? selected : '';
  presetDelete.disabled = !presetEl.value;
  presetEl.disabled = names.length === 0;
}

presetEl.addEventListener('change', () => {
  const name = presetEl.value;
  if (!name) return;
  const saved = presets.get(name);
  if (!saved) return paintPresets();
  // Reset first: a preset is the whole tuning, not a patch. Applying it over
  // whatever is currently set would leave anything the preset does not mention
  // exactly where it was, so two presets would compose into a third that neither
  // of them describes.
  resetTuning();
  applyTuning(saved);
  controls.refresh();
  presetName.value = name;
  presetDelete.disabled = false;
  publishTuning();
  updateTuneState();
  relanguage();
  engine.recorder.event(`preset:load ${name}`);
});

presetSave.addEventListener('click', () => {
  const name = presetName.value.trim() || presetEl.value;
  if (!name) {
    presetName.focus();
    return;
  }
  const stored = presets.put(name, tuning());
  if (!stored) return;
  presetName.value = stored;
  paintPresets(stored);
  engine.recorder.event(`preset:save ${stored}`);
});

presetDelete.addEventListener('click', () => {
  const name = presetEl.value;
  if (!name || !presets.remove(name)) return;
  presetName.value = '';
  paintPresets('');
  engine.recorder.event(`preset:delete ${name}`);
});

paintPresets();

function closeDrawer() {
  drawer.hidden = true;
}

document.getElementById('settings').addEventListener('click', (e) => {
  e.stopPropagation();
  drawer.hidden = !drawer.hidden;
  if (!drawer.hidden) controls.refresh();
});
document.getElementById('drawer-close').addEventListener('click', closeDrawer);
// The drawer sits over the stage, whose click handler starts and stops the take.
drawer.addEventListener('click', (e) => e.stopPropagation());
barEl.addEventListener('click', (e) => e.stopPropagation());

document.getElementById('export').addEventListener('click', async (e) => {
  e.stopPropagation();
  const btn = e.currentTarget;
  const previous = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'exporting…';
  try {
    await downloadCaptionSVG(caption);
    btn.textContent = 'exported';
  } catch (err) {
    trouble.set('export', String(err.message ?? err));
    paintState();
  } finally {
    setTimeout(() => {
      btn.textContent = previous;
      btn.disabled = false;
    }, 1200);
  }
});

updateTuneState();
publishTuning();
paintState();
// Honours ?view=face on arrival, and paints the button either way.
setView(view);
